import { hcr_saveHierarchyCompleted } from "contexts/hierarchy/functions/hcr_saveHierarchyCompleted";
import { hcr_updateHierarchy } from "contexts/hierarchy/functions/hcr_updateHierarchy";
import { SAVE_HIERARCHY_COMPLETED, UPDATE_HIERARCHY } from "contexts/hierarchy/interface";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { tlf_updateTlf } from "contexts/tlf/tlf_updateTlf";
import { toArsTlfSnapshot } from "contexts/tlf/toArsTlfSnapshot";
import { MosaicTocEntryDto, REFRESH } from "contexts/toc/interface";
import { toc_refresh } from "contexts/toc/toc_refresh";
import { eTlfItemType } from "functions/api/enums";
import { HierarchyUpdateDto } from "functions/api/hierarchy/saveHierarchyData";
import { toHierarchyUpdateDto } from "functions/api/hierarchy/toHierarchyUpdateDto";
import { hydrateArsTableBody } from "functions/api/tlfItem/hydrateArsTableBody";
import { isArsMasterReadDto, TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { applyVariantPatches } from "functions/tlf/applyVariantPatches";
import { VARIANT_UPDATEABLE_KEYS } from "functions/tlf/VariantUpdateableKeys";
import { toIndexDto } from "functions/toIndexDto";
import { cloneDeep, isEqual } from "lodash";
import { IAppActionSaveTlfCompleted, IUnifiedState } from "./interface";

/**
 * Reconciles hierarchy, TLF history and ToC data from a save belonging to the active editor.
 * Hydration operates on a clone, leaving both the previous state and action untouched.
 *
 * @param state - Current application state including the loaded hierarchy and TLF.
 * @param action - Saved server data, creation flag, source TLF ID and submitted hierarchy snapshot.
 * @returns Atomically reconciled state, or the unchanged state when the source editor has gone.
 */
export const app_saveTlfCompleted = (
  state: IUnifiedState,
  action: IAppActionSaveTlfCompleted,
): IUnifiedState => {
  // Check we are saving the active ID
  if (state.tlfState.tlfData?.id !== action.submittedTlfId) return state;

  let hierarchyState = state.hierarchyState;
  // Reapply only fields edited after submission, retaining server-normalised values elsewhere.
  const subsequentUpdates: Partial<HierarchyUpdateDto> =
    hierarchyState.hierarchy?.id === action.submittedHierarchy.id
      ? Object.fromEntries(
          Object.entries(toHierarchyUpdateDto(hierarchyState.hierarchy)).filter(
            ([key, value]) =>
              !isEqual(value, action.submittedHierarchy[key as keyof HierarchyUpdateDto]),
          ),
        )
      : {};
  const hasSubsequentUpdates = Object.keys(subsequentUpdates).length > 0;
  const savedMaster = cloneDeep(action.result.tlfItem);
  // Hydration belongs to the editor view, never the persisted master or its baseline.
  let savedTlf = cloneDeep(savedMaster);
  let variantCompositionFailed = false;
  let fallbackVariantUpdates: Partial<TlfItemReadDto> = {};
  if (action.submittedVariantView && isArsMasterReadDto(savedMaster)) {
    const composed = applyVariantPatches(
      savedMaster,
      savedMaster,
      state.tlfState.appliedVariantPatches,
    );
    if (composed.success) {
      savedTlf = composed.data;
    } else {
      variantCompositionFailed = true;
      // A fallback view is not a persisted DTO: retain only editable variant values as dirty edits.
      fallbackVariantUpdates = cloneDeep(
        Object.fromEntries(
          Object.entries(action.submittedVariantView).filter(
            ([key, value]) =>
              VARIANT_UPDATEABLE_KEYS.some((allowed) => allowed === key) &&
              !isEqual(value, savedMaster[key as keyof TlfItemReadDto]),
          ),
        ),
      );
    }
  }
  const returnedHierarchy = action.result.hierarchy;
  if (returnedHierarchy) {
    const existingHierarchy = hierarchyState.hierarchyNodes.find(
      (node) => node.id === returnedHierarchy.id,
    );
    const savedHierarchy = {
      ...returnedHierarchy,
      clinicalLibrary:
        hierarchyState.hierarchy?.id === returnedHierarchy.id
          ? hierarchyState.hierarchy.clinicalLibrary
          : existingHierarchy?.clinicalLibrary,
    };
    if (savedHierarchy.id === hierarchyState.id) {
      hierarchyState = hcr_saveHierarchyCompleted(hierarchyState, {
        operation: SAVE_HIERARCHY_COMPLETED,
        hierarchy: savedHierarchy,
      });
      if (hasSubsequentUpdates) {
        hierarchyState = hcr_updateHierarchy(hierarchyState, {
          operation: UPDATE_HIERARCHY,
          hierarchy: subsequentUpdates,
          isNameChanged: state.hierarchyState.isNameChanged,
        });
      }
    } else {
      hierarchyState = {
        ...hierarchyState,
        hierarchyNodes: [
          ...hierarchyState.hierarchyNodes.filter((node) => node.id !== savedHierarchy.id),
          savedHierarchy,
        ],
      };
    }
  }
  if (
    savedTlf.type === eTlfItemType.arsTable &&
    savedTlf.primaryHierarchyId === hierarchyState.id
  ) {
    hydrateArsTableBody(hierarchyState.hierarchyNodes, savedTlf);
  }
  const tlfItemIndex = [
    ...(hierarchyState.tlfItemIndex ?? []).filter(
      (item) => !(item.azsolId === savedTlf.azsolId && item.version === savedTlf.version),
    ),
    toIndexDto(savedMaster),
  ].sort((first, second) => first.azsolId.localeCompare(second.azsolId));
  const tocEntry: MosaicTocEntryDto = {
    ...savedMaster,
    changed: false,
    inError: false,
    changedTitles: savedMaster.changedTitles ?? [],
  };
  const submittedTlf = action.submittedTlf;
  let subsequentTlfUpdates: Partial<TlfItemReadDto> & Record<string, unknown> = {
    ...fallbackVariantUpdates,
    ...(submittedTlf && state.tlfState.tlfData
      ? Object.fromEntries(
          Object.entries(state.tlfState.tlfData).filter(
            ([key, value]) => !isEqual(value, submittedTlf[key as keyof TlfItemReadDto]),
          ),
        )
      : {}),
  };
  if (
    action.submittedVariantView &&
    isArsMasterReadDto(savedMaster) &&
    isArsMasterReadDto(state.tlfState.tlfData) &&
    isArsMasterReadDto(submittedTlf)
  ) {
    const currentMaster = state.tlfState.tlfData;
    const catalog = { ...savedMaster.allVariantPatches };
    const patchNames = new Set([
      ...Object.keys(submittedTlf.allVariantPatches),
      ...Object.keys(currentMaster.allVariantPatches),
    ]);
    for (const name of patchNames) {
      const currentPatch = currentMaster.allVariantPatches[name];
      if (!isEqual(currentPatch, submittedTlf.allVariantPatches[name])) {
        if (currentPatch) catalog[name] = cloneDeep(currentPatch);
        else delete catalog[name];
      } else if (!(name in catalog) && state.tlfState.appliedVariantPatches.includes(name)) {
        catalog[name] = cloneDeep(currentPatch);
      }
    }
    // Catalogue changes are reconciled per patch, never by replacing server-normalised metadata.
    delete subsequentTlfUpdates.allVariantPatches;
    if (!isEqual(catalog, savedMaster.allVariantPatches)) {
      // Compose using retained catalogue edits before restoring direct field edits.
      // These values remain a separate undo entry, not part of the persisted save point.
      const retainedMaster = { ...savedMaster, allVariantPatches: catalog };
      const composed = applyVariantPatches(
        retainedMaster,
        retainedMaster,
        state.tlfState.appliedVariantPatches,
      );
      const retainedView = composed.success
        ? composed.data
        : {
            ...retainedMaster,
            ...cloneDeep(
              Object.fromEntries(
                Object.entries(currentMaster).filter(([key]) =>
                  VARIANT_UPDATEABLE_KEYS.some((allowed) => allowed === key),
                ),
              ),
            ),
          };
      if (
        retainedView.type === eTlfItemType.arsTable &&
        retainedView.primaryHierarchyId === hierarchyState.id
      ) {
        hydrateArsTableBody(hierarchyState.hierarchyNodes, retainedView);
      }
      // Both values describe the same TLF item (checked via submittedTlfId), so they share a variant.
      // Object.assign is used because spreading the two unions yields cross-variant combinations
      // that TypeScript cannot assign, even though they cannot occur here.
      subsequentTlfUpdates = Object.assign({}, retainedView, subsequentTlfUpdates, {
        allVariantPatches: catalog,
      });
    }
  }
  const persistedTlfState = { ...state.tlfState };
  if (action.submittedVariantView && isArsMasterReadDto(savedMaster)) {
    // Undo to the save point must not reactivate patches that the server did not persist.
    persistedTlfState.appliedVariantPatches = variantCompositionFailed
      ? []
      : state.tlfState.appliedVariantPatches.filter(
          (name) => name in savedMaster.allVariantPatches,
        );
    persistedTlfState.activeVariantPatchName =
      !variantCompositionFailed &&
      state.tlfState.activeVariantPatchName !== null &&
      state.tlfState.activeVariantPatchName in savedMaster.allVariantPatches
        ? state.tlfState.activeVariantPatchName
        : null;
  }
  let tlfState = tlf_updateTlf(persistedTlfState, {
    operation: UPDATE_TLF,
    tlfData: savedTlf,
    newSavePoint: true,
    calledFrom: "app_saveTlfCompleted",
  });
  if (Object.keys(subsequentTlfUpdates).length > 0 || variantCompositionFailed) {
    tlfState = tlf_updateTlf(
      {
        ...tlfState,
        appliedVariantPatches: state.tlfState.appliedVariantPatches,
        activeVariantPatchName: state.tlfState.activeVariantPatchName,
      },
      {
        operation: UPDATE_TLF,
        tlfData: subsequentTlfUpdates,
        calledFrom: "app_saveTlfCompleted:RetainEdits",
      },
    );
  }
  return {
    ...state,
    hierarchyState: { ...hierarchyState, tlfItemIndex },
    tlfState: {
      ...tlfState,
      masterTlfData: isArsMasterReadDto(savedMaster)
        ? toArsTlfSnapshot(savedMaster)
        : tlfState.masterTlfData,
    },
    tocState: toc_refresh(state.tocState, {
      operation: REFRESH,
      hierarchyId: hierarchyState.id,
      items: [...state.tocState.tocItems.filter((item) => item.id !== savedTlf.id), tocEntry],
    }),
  };
};
